import { LinearProgress } from '@mui/material';
import { AnalyzingYourAttachmentsStepComponent } from 'api/types';
import AgathaMessage from 'components/AgathaMessage/AgathaMessage';
import { useUpdateConversation } from 'components/App/hooks';
import { useAppSelector } from 'hooks';
import { useEffect, useState } from 'react';
import { selectIsAuthorIconVisible } from 'reducers/workflows';
import { styled, keyframes } from '@mui/material/styles';

interface AnalyzingYourAttachmentsProps {
  isLastStep: boolean;
  stepIndex: number;
  widgetComponent: AnalyzingYourAttachmentsStepComponent;
}
const NUM_MANY_ATTACHMENTS = 4;

const buildMessage = (
  text: AnalyzingYourAttachmentsStepComponent['component_fields']['forethought_text'],
) => {
  return `${text.this_can_take_some_time} ${NUM_MANY_ATTACHMENTS} ${text.attachments}.`;
};

const useProgressTimer = ({
  durationMs,
  intervalMs,
  maxProgress,
}: {
  durationMs: number;
  intervalMs: number;
  maxProgress: number;
}) => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const startTime = Date.now();

    const timer = setInterval(() => {
      const elapsedTime = Date.now() - startTime;

      if (elapsedTime >= durationMs) {
        setProgress(maxProgress);
        clearInterval(timer);
        return;
      }

      const expectedProgress = (elapsedTime / durationMs) * maxProgress;
      setProgress(oldProgress => {
        const remainingProgress = expectedProgress - oldProgress;
        const randomIncrement = Math.random() * remainingProgress;
        const newProgress = oldProgress + randomIncrement;
        return Math.min(newProgress, maxProgress);
      });
    }, intervalMs);

    const finalTimeout = setTimeout(() => {
      // failsafe to ensure progress reaches maxProgress and ends the interval
      setProgress(maxProgress);
      clearInterval(timer);
    }, durationMs);

    return () => {
      clearInterval(timer);
      clearTimeout(finalTimeout);
    };
  }, [durationMs, maxProgress, intervalMs]);

  return progress;
};

export const AnalyzingYourAttachments = ({
  isLastStep,
  stepIndex,
  widgetComponent,
}: AnalyzingYourAttachmentsProps) => {
  const {
    component_fields: { forethought_text: forethoughtText },
  } = widgetComponent;
  const updateConversation = useUpdateConversation();
  const isAuthorIconVisible = useAppSelector(state =>
    selectIsAuthorIconVisible(state, stepIndex),
  );

  const progress = useProgressTimer({
    durationMs: 45_000,
    intervalMs: 750,
    maxProgress: 95,
  });

  useEffect(() => {
    updateConversation({}, { isContinueAutoflowIntent: true });
  }, [updateConversation]);

  return (
    <AgathaMessage
      isAuthorIconVisible={isAuthorIconVisible}
      stepIndex={stepIndex}
    >
      <>
        <AnimatedEllipsis
          animate={isLastStep}
          text={forethoughtText.analyzing_your_attachments}
        />
        <LinearProgress
          sx={{ borderRadius: '25px', my: '16px !important' }}
          value={isLastStep ? progress : 100}
          variant='determinate'
        />
        {buildMessage(forethoughtText)}
      </>
    </AgathaMessage>
  );
};

const dotFade = keyframes`
  0% { opacity: 0; }
  50% { opacity: 1; }
  100% { opacity: 0; }
`;

const Loading = styled('span')(() => ({
  overflow: 'hidden',
  position: 'relative',
  whiteSpace: 'nowrap',
}));

const AnimatedEllipsis = ({
  animate,
  text,
}: {
  animate: boolean;
  text: string;
}) => {
  const dots = Array.from({ length: 3 }, (_, i) => i);

  return (
    <Loading>
      {text}
      {dots.map(i => (
        <Dot animate={animate} delay={i} key={i}>
          .
        </Dot>
      ))}
    </Loading>
  );
};

const Dot = styled('span')(
  ({ animate, delay }: { animate: boolean; delay: number }) => ({
    animation: animate ? `${dotFade} 1s linear infinite` : undefined,
    animationDelay: `${delay * 0.3}s`,
    display: 'inline-block',
  }),
);
